
import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material';
import { useLocation, matchPath, useNavigate } from 'react-router-dom';
import { ThemeContext, DEFAULT_THEME, getShapeClasses } from '../context/ThemeContext.jsx';
import AlertNotificationCenter from './AlertNotificationCenter.jsx';
import UserProfileMenu from './UserProfileMenu.jsx';
import { DynamicIcon } from './icons.jsx';
import { LoadingSpinner } from './Common.jsx';
import { useData } from '../hooks/useData.js';
import { apiFetch } from '../api';
import { hasPermission, isTenantScoped } from '../utils/permissions';
// import TenantLayout from './TenantLayout.jsx'; // REMOVED (Cleaned up)

// --- Page Imports ---
import AdminDashboardPage from '../pages/AdminDashboardPage.jsx';
import FlowsPage from '../pages/FlowsPage.tsx';
import { STUDIOS } from '../constants/studios.js';

// These must match the content_types table. IDs never change; names can.
export const CONTENT_TYPE_IDS = {
   AI_ML: 1,  // AI/ML Predictions
   GENAI: 2,  // GenAI App
   DASHBOARD: 3,  // Dashboard
   WORKFLOW: 4,  // Workflow App
   DATASET: 5,  // Dataset Refresh
   ETL: 7,  // ETL
   OTHER: 99, // Other
};

// Helper: returns true if the given contentTypeId is an AI / ML / GenAI type
const isAiMlGenAi = (contentTypeId) =>
   contentTypeId === CONTENT_TYPE_IDS.AI_ML ||
   contentTypeId === CONTENT_TYPE_IDS.GENAI;

// Dashboard type (used for bundle content categorization)
const isDashboardType = (contentTypeId) =>
   contentTypeId === CONTENT_TYPE_IDS.DASHBOARD;

import DashboardsPage from '../pages/dashboard/DashboardsPage.tsx';
import DashboardsLandingPage from '../pages/dashboard/DashboardsLandingPage.tsx';
import DashboardViewPage from '../pages/dashboard/DashboardViewPage';
import PartnersPage from '../pages/PartnersPage.jsx';
import TenantsPage from '../pages/TenantsPage.jsx';
import UsersPage from '../pages/UsersPage.jsx';
import RolesPermissionsPage from '../pages/RolesPermissionsPage.jsx';
import BusinessAppsPage from '../pages/BusinessAppsPage.jsx';
import SettingsPage from '../pages/SettingsPage.jsx'; // Admin Settings
import MLModelStudioPage from '../pages/MLModelStudioPage.jsx';
import LandingPage from '../pages/LandingPage.jsx';
import ExecutionLogViewer from './logs/ExecutionLogViewer.jsx';
import TenantUserGuidePage from '../pages/TenantUserGuidePage.jsx';

import TenantDetailPage from '../pages/TenantDetailPage.jsx';
import ContentsPage from '../pages/ContentsPage.jsx';
import PartnerDetailPage from '../pages/PartnerDetailPage.jsx';
import AssetDetailPage from '../pages/AssetDetailPage.jsx';
import PlansPage from '../pages/PlansPage.jsx';
import DataWorkspacePage from '../pages/DataWorkspacePage/DataWorkspacePage.jsx';
import DatasetsPage from "../pages/DatasetsPage";
import DataSourcesPage from "../pages/DataSourcesPage";
// import DataModelsPage from "../pages/DataModelsPage.tsx"; // NEW: Canvas-based designer
import DataModelsPage from "../pages/DataModelsPage";
import AutomationsPage from '../pages/AutomationsPage';
import AlertRulesPage from '../pages/AlertRulesPage';
import DemoManagementPage from '../pages/DemoManagementPage.jsx';
import AiInsightPage from '../pages/AiInsightPage.jsx';
import SemanticKnowledgeBasePage from '../pages/SemanticKnowledgeBasePage.jsx';
import PlatformSettings from '../pages/PlatformSettings.jsx';
import MLAppRunnerPage from '../pages/MLAppRunnerPage.jsx';
import CreditsPage from '../pages/CreditsPage.jsx';
import SupportPage from '../pages/SupportPage.jsx';

import ProfilePage from '../pages/ProfilePage.jsx';
import DqProfilerPage from '../pages/DqProfilerPage.jsx';


// --- Auth Context ---
import { useAuth } from '../context/useAuth';

//tenant layout
import TenantOnboardingPage from "../pages/TenantOnboardingPage.jsx";

// --- TENANT SPECIFIC IMPORTS ---
import TenantSettingsPage from '../pages/TenantSettingsPage.jsx';
import TenantUsersPage from '../pages/tenants/TenantUsersPage';
import MyAppsPage from '../pages/tenants/MyAppsPage';
import TenantAppRunnerPage from '../pages/TenantAppRunnerPage';
import TenantMLStudioListPage from '../pages/TenantMLStudioListPage';
import TaskCenterPage from '../pages/tenants/TaskCenterPage.jsx';


// --- Page Permissions Map (Backend Sync) ---
const PAGE_PERMISSION_MAP = {
   dashboard: 'page:dashboard:view',
   tenants: 'page:tenants:view',
   partners: 'page:partners:view',
   users: 'page:users:view',
   roles_permissions: 'page:roles_permissions:view',
   business_apps: 'page:business_apps:view',
   contents: 'page:contents:view',
   DatasetsPage: 'page:datasets:view',
   datasources: 'page:datasources:view',
   datamodels: 'page:datamodels:view',
   plans: 'page:plans:view',
   platform_settings: 'page:settings:view',
   dashboards: 'page:dashboard:view', // Reuse standard permission
   flows: 'page:flows:view',
   automations: 'page:scheduler:view',
   alerts: 'page:alerts:view',
   demo_management: 'page:tenants:view',
   app_runner: 'page:contents:view',
   landing: null, // Landing page is always accessible to any authenticated user
   logs: 'page:flows:view', // Reusing flows permission for logs
   'ai-insight': 'ai_insights:view', // AI Insights
   'semantic-kb': 'knowledge_base:view', // Knowledge Base


   // Support Tickets ÃƒÆ’Ã†â€™Ãƒâ€šÃ‚Â¢ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â€šÂ¬Ã…Â¡Ãƒâ€šÃ‚Â¬ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â‚¬Å¡Ã‚Â¬Ãƒâ€šÃ‚Â accessible by admins and support agents
   support: 'page:support:view',

   // Unified Data Workspace
   'data-workspace': 'page:workspace:view',

   // Data Profiler
   'dq-profiler': 'page:dq_profiler:view',

   // Tenant Specific
   'my-apps': 'page:tenant:apps:view',
   'byor': 'page:tenant:byor:view', // Dashboards for tenants
   'byom': 'page:tenant:byom:view'  // ML Studio for tenants
};

const PlatformLayout = ({ token }) => {
   const { user: currentUser, loading: userLoading, refreshUser: refresh, logout, pwdExp } = useAuth();
   const userError = null; // AuthContext handles errors internally via token state
   const themeCtx = useContext(ThemeContext) || {};
   const { theme = DEFAULT_THEME, setTheme = () => { } } = themeCtx;
   const location = useLocation();
   const navigate = useNavigate();

   const [isSidebarOpen, setSidebarOpen] = useState(true);
   const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
   const [activePage, setActivePage] = useState(() => {
      const path = window.location.pathname;
      if (path === '/' || path === '') return 'landing';
      // Robust segment extraction (ignore leading slash)
      const segment = path.split('/').filter(Boolean)[0];
      return segment || 'dashboard';
   });
   const [profileTab, setProfileTab] = useState(null);

   const [editingContentId, setEditingContentId] = useState(null);
   const [viewingTenantId, setViewingTenantId] = useState(null);
   const [viewingPartnerId, setViewingPartnerId] = useState(null);
   const [viewingAsset, setViewingAsset] = useState(null);

   // Dashboard Editor State
   const [dashboardEditorState, setDashboardEditorState] = useState(null); // { mode: 'view' | 'edit' | 'new', dashboardId?: string }
   const [previewContentId, setPreviewContentId] = useState(null);

   //tenantonboarding
   const [onboardingTenantId, setOnboardingTenantId] = useState(null);

   // --- Password Expiry Banner Logic (For imminent expiry: Day 0-1) ---




   // --- TENANT SPECIFIC STATE ---
   // isTenantScoped uses roleScope JWT claim ÃƒÆ’Ã†â€™Ãƒâ€šÃ‚Â¢ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â€šÂ¬Ã…Â¡Ãƒâ€šÃ‚Â¬ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â‚¬Å¡Ã‚Â¬Ãƒâ€šÃ‚Â works for built-in (r4/r7) AND custom tenant roles
   const isTenantUser = isTenantScoped(currentUser);
   const [showTenantGuide, setShowTenantGuide] = useState(false);

   // Tenant content + features ÃƒÆ’Ã†â€™Ãƒâ€šÃ‚Â¢ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â€šÂ¬Ã…Â¡Ãƒâ€šÃ‚Â¬ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â‚¬Å¡Ã‚Â¬Ãƒâ€šÃ‚Â loaded in parallel once currentUser is ready
   const [myContent, setMyContent] = useState(null);
   const [contentLoading, setContentLoading] = useState(false);
   const refreshContent = React.useCallback(() => {
      if (!isTenantUser || !currentUser?.tenantId || !token) return;
      setContentLoading(true);
      apiFetch(`/tenants/${currentUser.tenantId}/my-content`, token)
         .then(data => setMyContent(data))
         .catch(e => console.error('[PlatformLayout] my-content refresh failed', e))
         .finally(() => setContentLoading(false));
   }, [isTenantUser, currentUser?.tenantId, token]);

   const [aidasAppsMenu, setAidasAppsMenu] = useState([]);
   const [publishedAppsMenu, setPublishedAppsMenu] = useState([]);
   const [myAppsMenu, setMyAppsMenu] = useState([]);
   const [allAppBundles, setAllAppBundles] = useState([]); // Kept for MyAppsPage compat, but now structured differently?
   const [allSections, setAllSections] = useState({ assigned_apps: [], dashboards: [], projects: [], apps: [] });

   // Tenant App Runner State
   const [tenantAppRunnerProps, setTenantAppRunnerProps] = useState(null);

   // --- SIDEBAR ACCORDION STATES (must live here, not inside inline Sidebar, to survive re-renders) ---
   const [isAidasAppsExpanded, setAidasAppsExpanded] = useState(() => {
      const saved = localStorage.getItem('platform_aidas_apps_expanded');
      return saved !== null ? JSON.parse(saved) : true;
   });
   const [isPublishedAppsExpanded, setPublishedAppsExpanded] = useState(() => {
      const saved = localStorage.getItem('platform_published_apps_expanded');
      return saved !== null ? JSON.parse(saved) : true;
   });
   const [isMyAppsExpanded, setMyAppsExpanded] = useState(() => {
      const saved = localStorage.getItem('platform_myapps_expanded');
      if (saved !== null) return JSON.parse(saved);
      const path = window.location.pathname;
      return path.startsWith('/tenant/app') || path.startsWith('/flows') || path.startsWith('/analytics/byor');
   });
   const [isSettingsExpanded, setSettingsExpanded] = useState(() => {
      const saved = localStorage.getItem('platform_settings_expanded');
      return saved !== null ? JSON.parse(saved) : false;
   });
   const [isAdminExpanded, setAdminExpanded] = useState(() => {
      const saved = localStorage.getItem('platform_admin_expanded');
      return saved !== null ? JSON.parse(saved) : false;
   });
   const [isCreatorExpanded, setCreatorExpanded] = useState(() => {
      const saved = localStorage.getItem('platform_creator_expanded');
      return saved !== null ? JSON.parse(saved) : true;
   });
   const [isAppStudioExpanded, setAppStudioExpanded] = useState(() => {
      const saved = localStorage.getItem('platform_app_studio_expanded');
      return saved !== null ? JSON.parse(saved) : true;
   });
   const [isDataExpanded, setDataExpanded] = useState(() => {
      const saved = localStorage.getItem('platform_data_expanded');
      return saved !== null ? JSON.parse(saved) : true;
   });

   // Stable ref-map for accordion group expand state inside the inline Sidebar.
   // useRef survives parent re-renders; sidebarExpandTick forces a re-render when map changes.
   const sidebarExpandedRef = React.useRef({});
   const [sidebarExpandTick, setSidebarExpandTick] = useState(0);
   // Persists nav scroll position across Sidebar remounts (Sidebar is inline so DOM resets on every parent render)
   const navScrollRef = React.useRef(0);

   // ... (Rest of state)

   // Sync accordion states to localStorage (so expand preference survives page refresh)
   useEffect(() => { localStorage.setItem('platform_aidas_apps_expanded', JSON.stringify(isAidasAppsExpanded)); }, [isAidasAppsExpanded]);
   useEffect(() => { localStorage.setItem('platform_published_apps_expanded', JSON.stringify(isPublishedAppsExpanded)); }, [isPublishedAppsExpanded]);
   useEffect(() => { localStorage.setItem('platform_myapps_expanded', JSON.stringify(isMyAppsExpanded)); }, [isMyAppsExpanded]);
   useEffect(() => { localStorage.setItem('platform_settings_expanded', JSON.stringify(isSettingsExpanded)); }, [isSettingsExpanded]);
   useEffect(() => { localStorage.setItem('platform_admin_expanded', JSON.stringify(isAdminExpanded)); }, [isAdminExpanded]);
   useEffect(() => { localStorage.setItem('platform_creator_expanded', JSON.stringify(isCreatorExpanded)); }, [isCreatorExpanded]);
   useEffect(() => { localStorage.setItem('platform_app_studio_expanded', JSON.stringify(isAppStudioExpanded)); }, [isAppStudioExpanded]);
   useEffect(() => { localStorage.setItem('platform_data_expanded', JSON.stringify(isDataExpanded)); }, [isDataExpanded]);


   const hasAccess = (pageId) => {
      // r1 bypass is handled inside hasPermission()
      if (isTenantUser) {
         // Platform-only pages -- never allow tenant users
         if (['roles_permissions', 'partners', 'tenants', 'plans', 'demo_management', 'platform_settings'].includes(pageId)) return false;

         // Always-open basic pages (no RBAC check -- predating permission system;
         // TODO: migrate to permission-gated once all custom tenant roles are audited)
         if (['landing', 'logs', 'settings', 'users', 'datasources', 'automations', 'alerts', 'credits', 'support'].includes(pageId)) return true;

         // My Apps -- always open for all tenants
         if (pageId === 'my-apps') return true;

         // Studio pages: gated by tenantFeatures only (tenant-level feature toggle)
         if (pageId === 'flows' || pageId === STUDIOS.WORKFLOW) return !!tenantFeatures?.workflow_studio;
         if (pageId === 'contents' || pageId === STUDIOS.ML)    return !!tenantFeatures?.ml_studio;
         if (pageId === STUDIOS.DATA)    return !!tenantFeatures?.data_studio;
         if (pageId === 'dashboards')    return !!tenantFeatures?.dashboard_studio;
         if (pageId === 'business_apps') return !!tenantFeatures?.app_marketplace;

         // Feature + RBAC: must be both feature-enabled AND permission-granted
         if (pageId === 'ai-insight')
            return !!(tenantFeatures?.ai_insight || tenantFeatures?.AIINSIGHT_NLQ) && hasPermission(currentUser, PAGE_PERMISSION_MAP['ai-insight'] ?? 'page:ai_insight:view');
         if (pageId === 'semantic-kb')
            return tenantFeatures?.semantic_kb !== false && hasPermission(currentUser, PAGE_PERMISSION_MAP['semantic-kb'] ?? 'page:knowledge_base:view');
         if (pageId === 'data-workspace')
            return !!(tenantFeatures?.unified_data_editor) && hasPermission(currentUser, PAGE_PERMISSION_MAP['data-workspace'] ?? 'page:workspace:view');

         // All other pages (dq-profiler + future pages): check PAGE_PERMISSION_MAP
         const requiredPermission = PAGE_PERMISSION_MAP[pageId];
         if (requiredPermission) return hasPermission(currentUser, requiredPermission);

         return true; // No permission defined = open page
      }

      // Granular Permission Check for Platform/Partner roles
      const requiredPermission = PAGE_PERMISSION_MAP[pageId];
      if (!requiredPermission) return true; // No permission required -- allow
      return hasPermission(currentUser, requiredPermission);
   };

   
   // --- NAVIGATION HELPERS ---
   const handleBack = () => {
      setViewingAsset(null);
      setEditingContentId(null);
      setOnboardingTenantId(null);
      setViewingTenantId(null);
      setViewingPartnerId(null);
      setDashboardEditorState(null);
      setPreviewContentId(null);
      setTenantAppRunnerProps(null);
   };

   const handleNavigate = (pageId, params = {}) => {
      handleBack();
      setActivePage(pageId);
      // Handle params if needed (e.g., setEditingContentId)
      if (params.contentId) setEditingContentId(params.contentId);
      if (params.tenantId) setViewingTenantId(params.tenantId);
      if (params.partnerId) setViewingPartnerId(params.partnerId);
      if (params.asset) setViewingAsset(params.asset);
   };

   const [tenantFeatures, setTenantFeatures] = useState({});

   // Fire features + my-content in PARALLEL — stale-while-revalidate via sessionStorage
   // First paint uses cached data (instant), then background fetch updates silently.
   useEffect(() => {
      if (!isTenantUser || !currentUser?.tenantId || !token) return;
      const tid = currentUser.tenantId;
      const CACHE_KEY = `apf_my_content_v2_${tid}`;
      const FEATURES_CACHE_KEY = `apf_tenant_features_v1_${tid}`;
      const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

      // ÃƒÆ’Ã†â€™Ãƒâ€šÃ‚Â¢ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â‚¬Å¡Ã‚Â¬Ãƒâ€šÃ‚ÂÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â€šÂ¬Ã…Â¡Ãƒâ€šÃ‚Â¬ÃƒÆ’Ã†â€™Ãƒâ€šÃ‚Â¢ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â‚¬Å¡Ã‚Â¬Ãƒâ€šÃ‚ÂÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â€šÂ¬Ã…Â¡Ãƒâ€šÃ‚Â¬ Step 1: Instant render from cache (if valid) ÃƒÆ’Ã†â€™Ãƒâ€šÃ‚Â¢ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â‚¬Å¡Ã‚Â¬Ãƒâ€šÃ‚ÂÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â€šÂ¬Ã…Â¡Ãƒâ€šÃ‚Â¬ÃƒÆ’Ã†â€™Ãƒâ€šÃ‚Â¢ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â‚¬Å¡Ã‚Â¬Ãƒâ€šÃ‚ÂÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â€šÂ¬Ã…Â¡Ãƒâ€šÃ‚Â¬ÃƒÆ’Ã†â€™Ãƒâ€šÃ‚Â¢ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â‚¬Å¡Ã‚Â¬Ãƒâ€šÃ‚ÂÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â€šÂ¬Ã…Â¡Ãƒâ€šÃ‚Â¬ÃƒÆ’Ã†â€™Ãƒâ€šÃ‚Â¢ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â‚¬Å¡Ã‚Â¬Ãƒâ€šÃ‚ÂÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â€šÂ¬Ã…Â¡Ãƒâ€šÃ‚Â¬ÃƒÆ’Ã†â€™Ãƒâ€šÃ‚Â¢ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â‚¬Å¡Ã‚Â¬Ãƒâ€šÃ‚ÂÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â€šÂ¬Ã…Â¡Ãƒâ€šÃ‚Â¬ÃƒÆ’Ã†â€™Ãƒâ€šÃ‚Â¢ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â‚¬Å¡Ã‚Â¬Ãƒâ€šÃ‚ÂÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â€šÂ¬Ã…Â¡Ãƒâ€šÃ‚Â¬ÃƒÆ’Ã†â€™Ãƒâ€šÃ‚Â¢ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â‚¬Å¡Ã‚Â¬Ãƒâ€šÃ‚ÂÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â€šÂ¬Ã…Â¡Ãƒâ€šÃ‚Â¬ÃƒÆ’Ã†â€™Ãƒâ€šÃ‚Â¢ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â‚¬Å¡Ã‚Â¬Ãƒâ€šÃ‚ÂÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â€šÂ¬Ã…Â¡Ãƒâ€šÃ‚Â¬ÃƒÆ’Ã†â€™Ãƒâ€šÃ‚Â¢ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â‚¬Å¡Ã‚Â¬Ãƒâ€šÃ‚ÂÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â€šÂ¬Ã…Â¡Ãƒâ€šÃ‚Â¬ÃƒÆ’Ã†â€™Ãƒâ€šÃ‚Â¢ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â‚¬Å¡Ã‚Â¬Ãƒâ€šÃ‚ÂÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â€šÂ¬Ã…Â¡Ãƒâ€šÃ‚Â¬ÃƒÆ’Ã†â€™Ãƒâ€šÃ‚Â¢ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â‚¬Å¡Ã‚Â¬Ãƒâ€šÃ‚ÂÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â€šÂ¬Ã…Â¡Ãƒâ€šÃ‚Â¬ÃƒÆ’Ã†â€™Ãƒâ€šÃ‚Â¢ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â‚¬Å¡Ã‚Â¬Ãƒâ€šÃ‚ÂÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â€šÂ¬Ã…Â¡Ãƒâ€šÃ‚Â¬ÃƒÆ’Ã†â€™Ãƒâ€šÃ‚Â¢ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â‚¬Å¡Ã‚Â¬Ãƒâ€šÃ‚ÂÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â€šÂ¬Ã…Â¡Ãƒâ€šÃ‚Â¬ÃƒÆ’Ã†â€™Ãƒâ€šÃ‚Â¢ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â‚¬Å¡Ã‚Â¬Ãƒâ€šÃ‚ÂÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â€šÂ¬Ã…Â¡Ãƒâ€šÃ‚Â¬ÃƒÆ’Ã†â€™Ãƒâ€šÃ‚Â¢ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â‚¬Å¡Ã‚Â¬Ãƒâ€šÃ‚ÂÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â€šÂ¬Ã…Â¡Ãƒâ€šÃ‚Â¬ÃƒÆ’Ã†â€™Ãƒâ€šÃ‚Â¢ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â‚¬Å¡Ã‚Â¬Ãƒâ€šÃ‚ÂÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â€šÂ¬Ã…Â¡Ãƒâ€šÃ‚Â¬ÃƒÆ’Ã†â€™Ãƒâ€šÃ‚Â¢ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â‚¬Å¡Ã‚Â¬Ãƒâ€šÃ‚ÂÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â€šÂ¬Ã…Â¡Ãƒâ€šÃ‚Â¬ÃƒÆ’Ã†â€™Ãƒâ€šÃ‚Â¢ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â‚¬Å¡Ã‚Â¬Ãƒâ€šÃ‚ÂÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â€šÂ¬Ã…Â¡Ãƒâ€šÃ‚Â¬ÃƒÆ’Ã†â€™Ãƒâ€šÃ‚Â¢ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â‚¬Å¡Ã‚Â¬Ãƒâ€šÃ‚ÂÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â€šÂ¬Ã…Â¡Ãƒâ€šÃ‚Â¬ÃƒÆ’Ã†â€™Ãƒâ€šÃ‚Â¢ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â‚¬Å¡Ã‚Â¬Ãƒâ€šÃ‚ÂÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â€šÂ¬Ã…Â¡Ãƒâ€šÃ‚Â¬ÃƒÆ’Ã†â€™Ãƒâ€šÃ‚Â¢ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â‚¬Å¡Ã‚Â¬Ãƒâ€šÃ‚ÂÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â€šÂ¬Ã…Â¡Ãƒâ€šÃ‚Â¬ÃƒÆ’Ã†â€™Ãƒâ€šÃ‚Â¢ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â‚¬Å¡Ã‚Â¬Ãƒâ€šÃ‚ÂÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â€šÂ¬Ã…Â¡Ãƒâ€šÃ‚Â¬
      try {
         const cached = sessionStorage.getItem(CACHE_KEY);
         if (cached) {
            const { data, ts } = JSON.parse(cached);
            if (Date.now() - ts < CACHE_TTL) {
               setMyContent(data); // paints sidebar immediately from cache
            }
         }
      } catch (_) { /* ignore parse errors */ }

      // -- Step 1b: Instant features from cache (SWR) --
      try {
         const cachedFeatures = sessionStorage.getItem(FEATURES_CACHE_KEY);
         if (cachedFeatures) {
            const { data, ts } = JSON.parse(cachedFeatures);
            if (Date.now() - ts < CACHE_TTL) {
               setTenantFeatures(data); // sidebar shows correct items immediately, no network wait
            }
         }
      } catch (_) { /* ignore parse errors */ }

      // -- Step 2: Background fetch -- updates cache + state silently --
      Promise.all([
         apiFetch(`/tenants/${tid}/features`, token).catch(e => { console.error('[PlatformLayout] features fetch failed', e); return {}; }),

         apiFetch(`/tenants/${tid}/my-content`, token).catch(e => { console.error('[PlatformLayout] my-content fetch failed', e); return null; }),

      ]).then(([features, content]) => {
         if (features) {
            setTenantFeatures(features);
            try {
               sessionStorage.setItem(FEATURES_CACHE_KEY, JSON.stringify({ data: features, ts: Date.now() }));
            } catch (_) { /* storage quota exceeded - skip */ }
         }
         if (content) {
            setMyContent(content);
            try {
               sessionStorage.setItem(CACHE_KEY, JSON.stringify({ data: content, ts: Date.now() }));
            } catch (_) { /* storage quota exceeded - skip */ }
         }
      }).finally(() => setContentLoading(false));
   }, [isTenantUser, currentUser?.tenantId, token]);

   // --- BUILD TENANT MENU (AIDAS Apps, Published Apps, My Apps) ---
   useEffect(() => {
      // console.log('[MyApps DEBUG] myContent=', myContent, 'isTenantUser=', isTenantUser);
      if (myContent && isTenantUser) {
         const { assigned_apps, dashboards, projects, apps } = myContent;
         // console.log('[MyApps DEBUG] assigned_apps=', assigned_apps);

         // Helper to check if item is Organization-Wide published
         const isGlobal = (item) => {
            try {
               if (!item.shared_with) return false;
               // Sometimes it's a JSON string from backend "[\"*\"]"
               const sw = typeof item.shared_with === 'string' ? JSON.parse(item.shared_with) : item.shared_with;
               return Array.isArray(sw) && sw.includes('*');
            } catch (err) {
               console.error("Failed to parse shared_with", err);
               return false;
            }
         };

         // 1. AIDAS Apps (System-Assigned Business Applications)
         const aidasMenu = [];

         // Keep track of all content IDs that are inside an App Bundle
         // so we don't accidentally list them again under Published/My Apps
         const bundledContentIds = new Set();

         if (assigned_apps && assigned_apps.length > 0) {
            assigned_apps.forEach(app => {
               const bundleName = app.name || 'App Bundle';
               const bundleContents = app.contents || [];

               const dashboards = bundleContents.filter(c => c.type === 'dashboard');
               // Workflow App (was App) -> 'app'
               const workflows = bundleContents.filter(c => c.type === 'content' && c.content_type === 'app');
               // AI/ML Predictions (was Project) -> 'project'
               const aiPredictions = bundleContents.filter(c => c.type === 'content' && c.content_type === 'project');

               bundleContents.forEach(c => bundledContentIds.add(c.id));

               const catChildren = [];

               // Dashboards Group
               if (dashboards.length > 0) {
                  catChildren.push({
                     id: `aidas_${app.id}_dash`,
                     label: 'DASHBOARDS',
                     type: 'group',
                     children: dashboards.map(d => ({
                        id: `bundle_item_${d.id}`,
                        label: d.name,
                        icon: 'LayoutDashboard',
                        action: () => {
                           sidebarExpandedRef.current['grp-aidas'] = true;
                           sidebarExpandedRef.current[`aidas_${app.id}`] = true;
                           sidebarExpandedRef.current[`aidas_${app.id}_dash`] = true;
                           setAidasAppsExpanded(true);
                           setActivePage('tenant_app_runner');
                           setTenantAppRunnerProps({ appUid: app.id, bundleId: app.id, contentUid: d.id, sectionKey: 'assigned_apps', sectionTitle: currentUser?.tenantName || 'My Apps' });
                           navigate('/', { state: { skipReset: true } });
                        }
                     }))
                  });
               }

               // AI/ML Predictions Group
               if (aiPredictions.length > 0) {
                  catChildren.push({
                     id: `aidas_${app.id}_ml`,
                     label: 'AI/ML PREDICTIONS',
                     type: 'group',
                     children: aiPredictions.map(f => ({
                        id: `bundle_item_${f.id}`,
                        label: f.name,
                        icon: 'BrainCircuit',
                        action: () => {
                           sidebarExpandedRef.current['grp-aidas'] = true;
                           sidebarExpandedRef.current[`aidas_${app.id}`] = true;
                           sidebarExpandedRef.current[`aidas_${app.id}_ml`] = true;
                           setAidasAppsExpanded(true);
                           setActivePage('tenant_app_runner');
                           setTenantAppRunnerProps({ appUid: app.id, bundleId: app.id, contentUid: f.id, sectionKey: 'assigned_apps', sectionTitle: currentUser?.tenantName || 'My Apps' });
                           navigate('/', { state: { skipReset: true } });
                        }
                     }))
                  });
               }

               bundleContents.forEach(c => bundledContentIds.add(c.id));

               // The navigate action ÃƒÆ’Ã†â€™Ãƒâ€šÃ‚Â¢ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â€šÂ¬Ã…Â¡Ãƒâ€šÃ‚Â¬ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â‚¬Å¡Ã‚Â¬Ãƒâ€šÃ‚Â opens the content gallery in the right panel
               const bundleNavAction = () => {
                  sidebarExpandedRef.current['grp-aidas'] = true;
                  sidebarExpandedRef.current[`aidas_${app.id}`] = true;
                  setAidasAppsExpanded(true);
                  setActivePage('tenant_app_runner');
                  setTenantAppRunnerProps({ appUid: app.id, bundleId: app.id, sectionKey: 'assigned_apps', sectionTitle: currentUser?.tenantName || 'My Apps' });
                  navigate('/', { state: { skipReset: true } });
               };

               const isDashContent = (c) =>
                  c.type === 'dashboard' ||
                  c.content_type === 'dashboard' ||
                  isDashboardType(c.contentTypeId);
               const dashChildren = bundleContents.filter(isDashContent);
               const allFlows = bundleContents.filter(c => !isDashContent(c));

               const aiMlGenAiFlows = allFlows.filter(c => isAiMlGenAi(c.contentTypeId));
               const workflowFlows = allFlows.filter(c => !isAiMlGenAi(c.contentTypeId));

               const childrenGroups = [];
               if (dashChildren.length > 0) {
                  childrenGroups.push({
                     id: `aidas_${app.id}_dash`,
                     label: 'Dashboards',
                     type: 'group',
                     children: dashChildren.map(c => ({
                        id: `bundle_${app.id}_dash_${c.id}`,
                        label: c.name,
                        icon: 'LayoutDashboard',
                        action: () => {
                           sidebarExpandedRef.current['grp-aidas'] = true;
                           sidebarExpandedRef.current[`aidas_${app.id}`] = true;
                           setAidasAppsExpanded(true);
                           setActivePage('tenant_app_runner');
                           setTenantAppRunnerProps({ appUid: app.id, contentUid: c.id, bundleId: app.id, sectionKey: 'assigned_apps', sectionTitle: currentUser?.tenantName || 'My Apps' });
                           navigate('/', { state: { skipReset: true } });
                        }
                     }))
                  });
               }

               if (aiMlGenAiFlows.length > 0) {
                  childrenGroups.push({
                     id: `aidas_${app.id}_aiml`,
                     label: 'AI/ML/GenAI',
                     type: 'group',
                     children: aiMlGenAiFlows.map(c => ({
                        id: `bundle_${app.id}_aiml_${c.id}`,
                        label: c.name,
                        icon: 'BrainCircuit',
                        action: () => {
                           sidebarExpandedRef.current['grp-aidas'] = true;
                           sidebarExpandedRef.current[`aidas_${app.id}`] = true;
                           setAidasAppsExpanded(true);
                           setActivePage('tenant_app_runner');
                           setTenantAppRunnerProps({ appUid: app.id, contentUid: c.id, bundleId: app.id, sectionKey: 'assigned_apps', sectionTitle: currentUser?.tenantName || 'My Apps' });
                           navigate('/', { state: { skipReset: true } });
                        }
                     }))
                  });
               }

               if (workflowFlows.length > 0) {
                  childrenGroups.push({
                     id: `aidas_${app.id}_flows`,
                     label: 'Workflows',
                     type: 'group',
                     children: workflowFlows.map(c => ({
                        id: `bundle_${app.id}_flow_${c.id}`,
                        label: c.name,
                        icon: 'Workflow',
                        action: () => {
                           sidebarExpandedRef.current['grp-aidas'] = true;
                           sidebarExpandedRef.current[`aidas_${app.id}`] = true;
                           setAidasAppsExpanded(true);
                           setActivePage('tenant_app_runner');
                           setTenantAppRunnerProps({ appUid: app.id, contentUid: c.id, bundleId: app.id, sectionKey: 'assigned_apps', sectionTitle: currentUser?.tenantName || 'My Apps' });
                           navigate('/', { state: { skipReset: true } });
                        }
                     }))
                  });
               }

               aidasMenu.push({
                  id: `aidas_${app.id}`,
                  label: bundleName,
                  icon: app.icon || 'FolderTree',
                  action: bundleNavAction,
                  children: childrenGroups.length > 0 ? childrenGroups : (catChildren.length > 0 ? catChildren : null)
               });
            });
         }

         // 2. Published Apps (Tenant-Wide Shared Apps)
         // 3. My Apps (User-Specific / Private Workspace Apps)
         const pubMenu = [];
         const myMenu = [];

         const buildCategoryGroup = (targetMenu, idPrefix, title, items, defaultIcon, getAction) => {
            if (items && items.length > 0) {
               targetMenu.push({
                  id: `${idPrefix}`,
                  label: title,
                  type: 'group',
                  children: items.map(item => ({
                     id: `${idPrefix}_item_${item.id}`,
                     label: item.name,
                     icon: defaultIcon,
                     action: getAction(item, idPrefix)
                  }))
               });
            }
         };

         // Filter out Dashboards/Projects/Apps that are already inside an App Bundle
         const displayDashboards = (dashboards || []).filter(d => !bundledContentIds.has(d.id));
         const displayFlows = [...(projects || []), ...(apps || [])].filter(p => !bundledContentIds.has(p.id));


         // Split Dashboards
         const allDash = displayDashboards;
         const pubDash = allDash.filter(d => isGlobal(d));
         const myDash = allDash.filter(d => !isGlobal(d));

         // Split AI/ML/Flows (Projects + Apps) using stable contentTypeId
         const allFlows = displayFlows;
         const aiMlFlows = allFlows.filter(f => isAiMlGenAi(f.contentTypeId));
         const workFlows = allFlows.filter(f => !isAiMlGenAi(f.contentTypeId));

         const pubAIML = aiMlFlows.filter(f => isGlobal(f));
         const myAIML = aiMlFlows.filter(f => !isGlobal(f));

         const pubWorkflows = workFlows.filter(f => isGlobal(f));
         const myWorkflows = workFlows.filter(f => !isGlobal(f));

         const dashAction = (d, prefix, groupName, setterState) => () => {
            sidebarExpandedRef.current[groupName] = true;
            sidebarExpandedRef.current[prefix] = true;
            setterState(true);
            setActivePage('myapps_dashboard');
            // Content records have dashboard_id pointing to the actual dashboard row;
            // native AppDashboard rows use their own id directly.
            const navigationId = d.dashboard_id || d.id;
            setDashboardEditorState({ mode: 'view', dashboardId: navigationId });
            navigate('/', { state: { skipReset: true } });
         };

         const flowAction = (f, prefix, groupName, setterState, sectionTitle) => () => {
            sidebarExpandedRef.current[groupName] = true;
            sidebarExpandedRef.current[prefix] = true;
            setterState(true);
            setActivePage('tenant_app_runner');
            setTenantAppRunnerProps({ appUid: f.id, contentUid: f.id, sectionKey: f.type === 'project' ? 'projects' : 'apps', sectionTitle: sectionTitle });
            navigate('/', { state: { skipReset: true } });
         };

         // Build Published Apps Menu
         buildCategoryGroup(pubMenu, 'pub_dash', 'Dashboards', pubDash, 'LayoutDashboard', (d, p) => dashAction(d, p, 'grp-published', setPublishedAppsExpanded));
         buildCategoryGroup(pubMenu, 'pub_aiml', 'AI/ML/GenAI', pubAIML, 'BrainCircuit', (f, p) => flowAction(f, p, 'grp-published', setPublishedAppsExpanded, 'Published Apps'));
         buildCategoryGroup(pubMenu, 'pub_flows', 'Workflows', pubWorkflows, 'Workflow', (f, p) => flowAction(f, p, 'grp-published', setPublishedAppsExpanded, 'Published Apps'));

         // Build My Apps Menu
         buildCategoryGroup(myMenu, 'my_dash', 'Dashboards', myDash, 'LayoutDashboard', (d, p) => dashAction(d, p, 'grp-myapps', setMyAppsExpanded));
         buildCategoryGroup(myMenu, 'my_aiml', 'AI/ML/GenAI', myAIML, 'BrainCircuit', (f, p) => flowAction(f, p, 'grp-myapps', setMyAppsExpanded, 'My Apps'));
         buildCategoryGroup(myMenu, 'my_flows', 'Workflows', myWorkflows, 'Workflow', (f, p) => flowAction(f, p, 'grp-myapps', setMyAppsExpanded, 'My Apps'));

         setAidasAppsMenu(aidasMenu);
         setPublishedAppsMenu(pubMenu);
         setMyAppsMenu(myMenu);

         setAllAppBundles(assigned_apps || []);
         // keep raw sections for TenantAppRunnerPage allSections prop
         setAllSections({ assigned_apps: assigned_apps || [], dashboards: dashboards || [], projects: projects || [], apps: apps || [] });
      }
   }, [myContent, isTenantUser, navigate]);


   useEffect(() => {
      if (currentUser) {
         if (currentUser.theme_settings) {
            setTheme(prevTheme => ({ ...DEFAULT_THEME, ...currentUser.theme_settings }));
         }

         const path = location.pathname;
         // --- MANDATORY DIAGNOSTIC LOGS [dbg001] ---
         // console.group('%c[Routing Debug]', 'color: #8b5cf6; font-weight: bold;');
         // console.log('Is Authenticated:', !!currentUser);
         // console.log('Token Exists:', !!token);
         // console.log('Current Path Before Reload:', path);
         // console.log('Active Page (Restored):', activePage);
         // console.log('Location State:', location.state);
         // console.groupEnd();

         const profileMatch = matchPath({ path: '/profile', end: true }, path);
         if (profileMatch) {
            setProfileTab(location.state?.forceTab || null);
            setActivePage('profile');
            return;
         }

         const appRunnerMatch = matchPath({ path: '/tenant/app/:appUid/:contentUid?', end: true }, path);
         if (appRunnerMatch && (isTenantUser || currentUser?.roleId === 'r1')) {
            setActivePage('tenant_app_runner');
            setTenantAppRunnerProps({
               appUid: appRunnerMatch.params.appUid,
               contentUid: appRunnerMatch.params.contentUid,
               bundleId: appRunnerMatch.params.appUid // Implicit convention
            });
            return;
         }

         // 2. Dashboard Standalone Handling (View/Edit)
         const dbViewMatch = matchPath({ path: '/analytics/byor/:id', end: true }, path);
         if (dbViewMatch) {
            setActivePage('dashboards');
            setDashboardEditorState({ mode: 'view', dashboardId: dbViewMatch.params.id });
            return;
         }

         const dbEditMatch = matchPath({ path: '/analytics/byor/:id/edit', end: true }, path);
         if (dbEditMatch) {
            setActivePage('dashboards');
            setDashboardEditorState({ mode: 'edit', dashboardId: dbEditMatch.params.id });
            return;
         }

         // 2b. ML Model Studio Handling (BYoM)
         const mlMatch = matchPath({ path: '/analytics/byom/:id', end: true }, path);
         if (mlMatch) {
            setActivePage('ml_studio');
            setEditingContentId(mlMatch.params.id);
            return;
         }

         // 2c. Flow Editor / Runner Matching (Projects)
         // Matches /flows/123 or /flows/123?mode=...
         const flowMatch = matchPath({ path: '/flows/:flowId', end: false }, path);
         if (flowMatch) {
            // Check if it's a known "Tool" page first (Data/ML/Workflow Studio)
            // But usually those trigger from /flows?mode=... which is caught by generic logic if mode is query param?
            // Actually, Studios link to /flows?mode=... 
            // Projects link to /flows/:id
            setActivePage('flows');
            return;
         }

         // 3. Generic Page Matching
         // If we navigated to '/' with skipReset (e.g. from My Apps), don't force Landing
         if (path === '/') {
            if (location.state?.skipReset) return;
            // Tenant users go to their landing page; platform/partner admins go to the admin dashboard
            if (isTenantUser) {
               setActivePage('landing');
            } else {
               setActivePage('dashboard'); // hits default ÃƒÂ¢Ã¢â‚¬Â Ã¢â‚¬â„¢ AdminDashboardPage
            }
            return;
         }

         const potentialPage = path.substring(1); // remove leading slash
         // Check for multi-segment paths like /dq/profiler
         if (path === '/dq/profiler') {
            if (hasAccess('dq-profiler')) setActivePage('dq-profiler');
            return;
         }
         // Simple matching for pages that map 1:1 with ID
         if (potentialPage) {
            // Check if it's a known page
            if (hasAccess(potentialPage) ||
               ['tenants', 'partners', 'users', 'roles_permissions', 'plans', 'datasources', 'automations', 'alerts', 'settings', 'logs', 'my-apps', 'dashboard', 'administration'].includes(potentialPage)) {
               setActivePage(potentialPage);
               return;
            }
         }

         // Redirect if landing page is restricted (e.g. for pure API users or special roles?)
         if (currentUser && !userLoading && !hasAccess(activePage)) {
            console.warn(`[Routing] Restricted access to "${activePage}". Triggering fallback...`);
            if (isTenantUser) setActivePage('landing');
            else {
               const firstPage = Object.keys(PAGE_PERMISSION_MAP).find(p => hasAccess(p));
               if (firstPage) setActivePage(firstPage);
            }
         }
      }
   }, [currentUser, setTheme, location, isTenantUser]);

   if (userLoading || !currentUser) {
      return (
         <div className="flex items-center justify-center h-screen">
            <LoadingSpinner />
            <span className="ml-3 text-gray-500">Loading user session...</span>
         </div>
      );
   }

   if (userError) {
      return (
         <div className="flex items-center justify-center h-screen text-red-500">
            Error loading user data. Please try logging in again.
         </div>
      );
   }


   const renderPage = () => {
      // 1. Asset/Detail Views (Overlays/Full Page modes)
      if (viewingAsset) {
         return <AssetDetailPage token={token} assetId={viewingAsset.id} assetType={viewingAsset.type} onBack={handleBack} />;
      }

      if (editingContentId) {
         if (activePage === 'ml_studio') {
            return <MLModelStudioPage token={token} contentId={editingContentId} onBack={() => { handleBack(); setActivePage('contents'); }} theme={theme} />;
         }
      }

      if (onboardingTenantId) {
         return (
            <TenantOnboardingPage
               token={token}
               tenantId={onboardingTenantId}
               onBack={() => setOnboardingTenantId(null)}
            />
         );
      }

      if (viewingTenantId) {
         return <TenantDetailPage token={token} tenantId={viewingTenantId} onBack={handleBack} currentUser={currentUser} />;
      }
      if (viewingPartnerId) {
         return <PartnerDetailPage token={token} partnerId={viewingPartnerId} onBack={handleBack} currentUser={currentUser} />;
      }

      // 2. Tenant Specific Routes
      if (isTenantUser) {
         // console.log("PLATFORM_LAYOUT renderPage isTenantUser true, activePage:", activePage);
         switch (activePage) {
            case 'landing': return <LandingPage setActivePage={setActivePage} />;
            case 'logs': return <ExecutionLogViewer />;
            case 'my-apps': return <MyAppsPage appBundles={allAppBundles} bundleId={tenantAppRunnerProps?.bundleId} />;
            case 'tenant_app_master':
               return <TenantAppRunnerPage
                  key={location.key}
                  navMode="master"
                  allSections={allSections}
               />;
            case 'tenant_app_section':
               return <TenantAppRunnerPage
                  key={location.key}
                  navMode="section"
                  sectionKey={tenantAppRunnerProps?.sectionKey}
                  sectionTitle={tenantAppRunnerProps?.sectionTitle}
                  sectionItems={tenantAppRunnerProps?.sectionItems}
                  allSections={allSections}
               />;
            case 'tenant_app_runner': return <TenantAppRunnerPage
               key={location.key}
               navMode="app"
               appUid={tenantAppRunnerProps?.appUid}
               contentUid={tenantAppRunnerProps?.contentUid}
               sectionKey={tenantAppRunnerProps?.sectionKey}
               sectionTitle={tenantAppRunnerProps?.sectionTitle}
               allSections={allSections}
            />;
            case 'tasks': return <TaskCenterPage token={token} currentUser={currentUser} />;
            case 'users': return <TenantUsersPage currentUser={currentUser} token={token} />;
            case 'automations': return <AutomationsPage token={token} currentUser={currentUser} />;
            case 'alerts': return <AlertRulesPage token={token} currentUser={currentUser} />;
            case 'settings': return <TenantSettingsPage token={token} />;
            case 'datasources': return <DataSourcesPage />;
            case 'datamodels': return <DataModelsPage {...{ token, currentUser, onNavigate: handleNavigate, refreshUser: refresh }} />;
            case 'datasets': return <DatasetsPage {...{ token, currentUser, onNavigate: handleNavigate, refreshUser: refresh }} />;
            case 'myapps_dashboard': // My Apps section dashboards ÃƒÆ’Ã†â€™Ãƒâ€šÃ‚Â¢ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â€šÂ¬Ã…Â¡Ãƒâ€šÃ‚Â¬ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â‚¬Å¡Ã‚Â¬Ãƒâ€šÃ‚Â renders read-only view
               if (dashboardEditorState?.dashboardId) {
                  return <DashboardViewPage
                     dashboardId={dashboardEditorState.dashboardId}
                     onBack={() => { setDashboardEditorState(null); setActivePage('dashboards'); }}
                     hideNavChips={true}
                  />;
               }
               return <LandingPage setActivePage={setActivePage} />;
            case 'dashboards':
               if (dashboardEditorState) {
                  if (dashboardEditorState.mode === 'view') {
                     return <DashboardViewPage
                        dashboardId={dashboardEditorState.dashboardId}
                        onBack={() => setDashboardEditorState(null)}
                        onEdit={(id) => setDashboardEditorState({ mode: 'edit', dashboardId: id })}
                        hideNavChips={true}
                     />;
                  }
                  return <DashboardsPage
                     dashboardId={dashboardEditorState.dashboardId}
                     onBack={() => setDashboardEditorState(null)}
                     onSaveComplete={() => setDashboardEditorState(null)}
                  />;
               }
               return <DashboardsLandingPage
                  {...{ token, currentUser, onNavigate: handleNavigate, refreshUser: refresh }}
                  onCreateNew={() => setDashboardEditorState({ mode: 'new' })}
                  onEdit={(id) => setDashboardEditorState({ mode: 'edit', dashboardId: id })}
                  onView={(id) => setDashboardEditorState({ mode: 'view', dashboardId: id })}
               />;
            case 'ml_models': return <TenantMLStudioListPage token={token} onNavigate={handleNavigate} />;
            case 'flows': return <FlowsPage studioMode={STUDIOS.ALL} token={token} currentUser={currentUser} onNavigate={handleNavigate} />;
            case 'ai-insight': return <AiInsightPage />;
            case 'semantic-kb': return <SemanticKnowledgeBasePage />;

            // Studio Routes
            case STUDIOS.DATA: return <FlowsPage studioMode={STUDIOS.DATA} token={token} currentUser={currentUser} onNavigate={handleNavigate} />;
            case STUDIOS.ML: return <FlowsPage studioMode={STUDIOS.ML} token={token} currentUser={currentUser} onNavigate={handleNavigate} />;
            case STUDIOS.WORKFLOW: return <FlowsPage studioMode={STUDIOS.WORKFLOW} token={token} currentUser={currentUser} onNavigate={handleNavigate} />;
            case 'credits': return <CreditsPage isAdminView={false} />;
            case 'support': return <SupportPage user={currentUser} />;
            case 'profile': return <ProfilePage forceTab={profileTab} onBack={() => setActivePage('landing')} />;
            default: return <LandingPage setActivePage={setActivePage} />;

         }
      }

      // 3. Support Agent Routes (r8 L1, r9 L2, r10 L3, r5 Support Admin)
      const isSupportAgentRole = ['r8', 'r9', 'r10', 'r5'].includes(currentUser?.roleId);
      if (isSupportAgentRole) {
         switch (activePage) {
            case 'support': return <SupportPage user={currentUser} />;
            case 'tasks': return <TaskCenterPage token={token} currentUser={currentUser} />;
            default: return <SupportPage user={currentUser} />;
         }
      }

      // 4. Platform Admin Routes (Existing)
      if (!hasAccess(activePage)) {
         // Show access-denied message instead of an infinite spinner
         return (
            <div className="flex flex-col h-screen items-center justify-center text-gray-500">
               <span style={{ fontSize: 48 }}>ÃƒÆ’Ã†â€™Ãƒâ€šÃ‚Â°ÃƒÆ’Ã¢â‚¬Â¦Ãƒâ€šÃ‚Â¸ÃƒÆ’Ã¢â‚¬Â¦Ãƒâ€šÃ‚Â¡ÃƒÆ’Ã¢â‚¬Å¡Ãƒâ€šÃ‚Â«</span>
               <p className="mt-4 text-lg font-semibold">Access Denied</p>
               <p className="text-sm mt-1">You do not have permission to view this page.</p>
            </div>
         );
      }

      const pageProps = { token, currentUser, onNavigate: handleNavigate, refreshUser: refresh };

      switch (activePage) {
         case 'tenants':
            return (
               <TenantsPage
                  {...pageProps}
                  onSelectTenant={setViewingTenantId}
                  onSelectOnboarding={setOnboardingTenantId}
               />
            );

         case 'partners': return <PartnersPage {...pageProps} onSelectPartner={setViewingPartnerId} />;
         case 'business_apps': return <BusinessAppsPage {...pageProps} onSelectAsset={setViewingAsset} onSelectContent={setEditingContentId} />;
         // AI/ML page hidden (Scenario A) ÃƒÆ’Ã†â€™Ãƒâ€šÃ‚Â¢ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â€šÂ¬Ã…Â¡Ãƒâ€šÃ‚Â¬ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â‚¬Å¡Ã‚Â¬Ãƒâ€šÃ‚Â redirect to home
         case 'contents': setActivePage('dashboard'); return null;
         case 'ai-insight': return <AiInsightPage />;
         case 'semantic-kb': return <SemanticKnowledgeBasePage />;
         case 'users': return <UsersPage {...pageProps} />;
         case 'roles_permissions': return <RolesPermissionsPage {...pageProps} />;
         case 'plans': return <PlansPage {...pageProps} />;
         case 'data-workspace': return <DataWorkspacePage {...pageProps} />;
         case 'dashboards':
            // If editing/viewing a specific dashboard, show editor or view
            if (dashboardEditorState) {
               if (dashboardEditorState.mode === 'view') {
                  // Render Dedicated View Page
                  return <DashboardViewPage
                     dashboardId={dashboardEditorState.dashboardId}
                     onBack={() => setDashboardEditorState(null)}
                     onEdit={() => setDashboardEditorState({ mode: 'edit', dashboardId: dashboardEditorState.dashboardId })}
                     hideNavChips={true}
                  />;
               }

               return <DashboardsPage
                  {...pageProps}
                  dashboardId={dashboardEditorState.dashboardId}
                  isEditMode={dashboardEditorState.mode === 'edit' || dashboardEditorState.mode === 'new'}
                  isNewDashboard={dashboardEditorState.mode === 'new'}
                  onBack={() => setDashboardEditorState(null)}
                  onSaveComplete={() => setDashboardEditorState(null)}
               />;
            }
            // Otherwise show landing page
            return <DashboardsLandingPage
               {...pageProps}
               onCreateNew={() => setDashboardEditorState({ mode: 'new' })}
               onEdit={(id) => setDashboardEditorState({ mode: 'edit', dashboardId: id })}
               onView={(id) => setDashboardEditorState({ mode: 'view', dashboardId: id })}
            />;
         case 'flows': return <FlowsPage {...pageProps} />;
         case 'datasources': return <DataSourcesPage />;
         // Admins ÃƒÂ¢Ã¢â‚¬Â Ã¢â‚¬â„¢ full platform settings (General/Integrations/Relay/Appearance/Security)
         // Tenant users ÃƒÂ¢Ã¢â‚¬Â Ã¢â‚¬â„¢ lightweight personal settings (Appearance/Security only)
         case 'settings': return isTenantUser
            ? <SettingsPage {...pageProps} />
            : <PlatformSettings {...pageProps} />;
         case 'datamodels': return <DataModelsPage {...pageProps} />;
         case 'DatasetsPage': return <DatasetsPage {...pageProps} />;
         case 'automations': return <AutomationsPage {...pageProps} />;
         case 'alerts': return <AlertRulesPage {...pageProps} />;
         case 'credits': return <CreditsPage isAdminView={currentUser?.roleId === 'r3' ? false : true} />;
         case 'support': return <SupportPage user={currentUser} />;
         case 'profile': return <ProfilePage onBack={() => setActivePage('dashboard')} />;
         case 'dq-profiler': return <DqProfilerPage />;
         case 'demo_management': return <DemoManagementPage {...pageProps} />;

         case 'platform_settings': return <PlatformSettings {...pageProps} />; // explicit alias
         case 'app_runner': {
            return <MLAppRunnerPage contentId={previewContentId} key={previewContentId} onBack={handleBack} />;
         }
         case 'tenant_app_runner': {
            return <TenantAppRunnerPage appUid={tenantAppRunnerProps?.appUid} contentUid={tenantAppRunnerProps?.contentUid} />;
         }
         case 'landing': return <LandingPage setActivePage={setActivePage} />;
         case 'logs': return <ExecutionLogViewer />;
         case 'dashboard':        // Platform Admin home ÃƒÂ¢Ã¢â€šÂ¬Ã¢â‚¬Â explicit route
         case 'administration':   // Alias: /administration URL
         default: return <AdminDashboardPage {...pageProps} setActivePage={setActivePage} user={currentUser} />;
      }
   };

   const Sidebar = () => {
      const shapeClasses = getShapeClasses(theme.shape);

      const NavLink = ({ iconName, label, page, action, children }) => {
         const isActive = activePage === page;
         const hasChildren = children && children.length > 0;
         const [isExpanded, setExpanded] = useState(false);

         if (hasChildren && isSidebarOpen) {
            return (
               <div className="mb-1">
                  <button
                     onClick={() => setExpanded(!isExpanded)}
                     className={`w-full flex items-center px-4 py-3 rounded-lg transition-colors duration-200 group text-gray-600 dark:text-gray-300 hover:bg-black/5 dark:hover:bg-white/5`}
                  >
                     <DynamicIcon name={iconName} className={`w-5 h-5 min-w-[20px] mr-3 ${isExpanded ? 'text-blue-600' : 'text-gray-500'}`} />
                     <span className="font-medium whitespace-nowrap flex-1 text-left">{label}</span>
                     <DynamicIcon name="ChevronDown" className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                  </button>
                  <div className={`overflow-hidden transition-all duration-300 ${isExpanded ? 'max-h-[5000px] opacity-100' : 'max-h-0 opacity-0'}`}>
                     {children.map(child => (
                        // Determine if child is a group or item
                        child.type === 'group' ? (
                           <div key={child.id} className="pl-4">
                              <div className="px-4 py-1 text-xs font-bold text-gray-400 uppercase">{child.label}</div>
                              {child.children?.map(subChild => (
                                 <button
                                    key={subChild.id}
                                    onClick={() => {
                                       if (subChild.action) subChild.action();
                                       else {
                                          // Default: Navigate to the page ID
                                          setActivePage(subChild.id);
                                       }
                                       setMobileSidebarOpen(false);
                                    }}
                                    className="w-full flex items-center px-4 py-2 rounded-lg hover:bg-black/5 dark:hover:bg-white/5 text-gray-600 dark:text-gray-300 ml-2"
                                 >
                                    <DynamicIcon name={subChild.icon} className="w-4 h-4 mr-2 opacity-70" />
                                    <span className="text-sm">{subChild.label}</span>
                                 </button>
                              ))}
                           </div>
                        ) : (
                           <button
                              key={child.id}
                              onClick={() => {
                                 if (child.action) child.action();
                                 else navigate(child.id === 'landing' ? '/' : `/${child.id}`);
                                 setMobileSidebarOpen(false);
                              }}
                              className="w-full flex items-center px-4 py-2 rounded-lg hover:bg-black/5 dark:hover:bg-white/5 text-gray-600 dark:text-gray-300 pl-12"
                           >
                              <span className="text-sm">{child.label}</span>
                           </button>
                        )
                     ))}
                  </div>
               </div>
            )
         }

         return (
            <button
               onClick={() => {
                  if (action) {
                     action();
                  } else {
                     handleBack();
                     // Navigate!
                     navigate(page === 'landing' ? '/' : `/${page}`);
                     setMobileSidebarOpen(false);
                  }
               }}
               className={`w-full flex items-center px-4 py-3 rounded-lg transition-colors duration-200 group ${isActive
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'text-gray-600 dark:text-gray-300 hover:bg-black/5 dark:hover:bg-white/5'
                  }`}
               title={!isSidebarOpen ? label : ''}
            >
               <DynamicIcon name={iconName} className={`w-5 h-5 min-w-[20px] mr-3 overflow-visible transition-transform group-hover:scale-110 ${isActive ? 'text-white' : 'text-gray-500 dark:text-gray-400 group-hover:text-blue-600 dark:group-hover:text-blue-400'}`} />
               <span className={`font-medium whitespace-nowrap transition-all duration-300 ${!isSidebarOpen ? 'opacity-0 w-0 overflow-hidden' : 'opacity-100'}`}>
                  {label}
               </span>
            </button>
         );
      };

      // Re-usable NavLink Renderer 
      const RenderMenuItem = React.useMemo(() => {
         return function MenuItem({ item }) {
            // Active Logic:
            // 1. Exact Match (Page ID)
            // 2. Tenant App Runner (App/Bundle ID)
            // 3. Flow/Project Editor (Flow ID)

            // Extract ID from URL for advanced matching
            const pathParts = location.pathname.split('/');
            const currentId = pathParts.length > 2 ? pathParts[pathParts.length - 1] : null;

            let isActive = activePage === item.id;

            if (activePage === 'tenant_app_runner') {
               // Match individual app/bundle/project items by their constructed IDs
               const uid = tenantAppRunnerProps?.appUid;
               const bid = tenantAppRunnerProps?.bundleId;
               isActive =
                  item.id === `app_${uid}` ||
                  item.id === `bundle_${bid}` ||
                  item.id === `proj_${uid}`;
            } else if (activePage === 'myapps_dashboard') {
               // Match specific dashboard item ÃƒÆ’Ã†â€™Ãƒâ€šÃ‚Â¢ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â€šÂ¬Ã…Â¡Ãƒâ€šÃ‚Â¬ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â‚¬Å¡Ã‚Â¬Ãƒâ€šÃ‚Â format: dash_{dashboardId}
               const did = dashboardEditorState?.dashboardId;
               isActive = item.id === `dash_${did}`;
            }

            const hasChildren = item.children && item.children.length > 0;

            // Check if any descendent child (possibly nested in groups) is active ÃƒÆ’Ã†â€™Ãƒâ€šÃ‚Â¢ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â€šÂ¬Ã…Â¡Ãƒâ€šÃ‚Â¬ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â‚¬Å¡Ã‚Â¬Ãƒâ€šÃ‚Â so we auto-expand
            const hasActiveChild = hasChildren && item.children.some(child => {
               if (child.type === 'group') {
                  return child.children?.some(sub => {
                     if (activePage === 'tenant_app_runner') {
                        const uid = tenantAppRunnerProps?.appUid;
                        const bid = tenantAppRunnerProps?.bundleId;
                        return sub.id === `app_${uid}` || sub.id === `bundle_${bid}` || sub.id === `proj_${uid}`;
                     }
                     if (activePage === 'myapps_dashboard') {
                        return sub.id === `dash_${dashboardEditorState?.dashboardId}`;
                     }
                     return activePage === sub.id;
                  });
               }
               if (activePage === 'tenant_app_runner') {
                  const uid = tenantAppRunnerProps?.appUid;
                  const bid = tenantAppRunnerProps?.bundleId;
                  return child.id === `app_${uid}` || child.id === `bundle_${bid}` || child.id === `proj_${uid}`;
               }
               if (activePage === 'myapps_dashboard') {
                  return child.id === `dash_${dashboardEditorState?.dashboardId}`;
               }
               return activePage === child.id;

            });

            // localExpanded lives in a stable ref at PlatformLayout level so it survives parent re-renders.
            const localExpanded = (item.id in sidebarExpandedRef.current)
               ? sidebarExpandedRef.current[item.id]
               : hasActiveChild;
            const setLocalExpanded = (valOrFn) => {
               const prev = sidebarExpandedRef.current[item.id] ?? hasActiveChild;
               const next = typeof valOrFn === 'function' ? valOrFn(prev) : valOrFn;
               sidebarExpandedRef.current[item.id] = next;
               setSidebarExpandTick(t => t + 1); // trigger re-render
            };
            // Auto-expand when active child changes
            React.useEffect(() => { if (hasActiveChild && !sidebarExpandedRef.current[item.id]) { sidebarExpandedRef.current[item.id] = true; setSidebarExpandTick(t => t + 1); } }, [hasActiveChild]);

            if (hasChildren && isSidebarOpen) {
               return (
                  <div className="mb-1">
                     {/* Label text ÃƒÆ’Ã†â€™Ãƒâ€šÃ‚Â¢ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â‚¬Å¡Ã‚Â¬Ãƒâ€šÃ‚Â ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â‚¬Å¡Ã‚Â¬ÃƒÂ¢Ã¢â‚¬Å¾Ã‚Â¢ section gallery; chevron ÃƒÆ’Ã†â€™Ãƒâ€šÃ‚Â¢ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â‚¬Å¡Ã‚Â¬Ãƒâ€šÃ‚Â ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â‚¬Å¡Ã‚Â¬ÃƒÂ¢Ã¢â‚¬Å¾Ã‚Â¢ accordion toggle */}
                     <div className={`w-full flex items-center px-4 py-2.5 rounded-lg transition-colors group ${hasActiveChild ? 'text-blue-600 dark:text-blue-400 font-semibold' : 'text-gray-600 dark:text-gray-300'}`}>
                        <DynamicIcon name={item.icon} className={`w-5 h-5 mr-3 shrink-0 ${hasActiveChild ? 'text-blue-500' : 'text-gray-500'}`} />
                        <button
                           className="font-medium whitespace-nowrap flex-1 text-left text-sm hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                           onClick={() => { if (item.sectionAction) item.sectionAction(); else if (item.action) { if (!localExpanded) { item.action(); setLocalExpanded(true); } else { setLocalExpanded(false); } } else setLocalExpanded(v => !v); }}
                        >
                           {item.label}
                        </button>
                        <button onClick={() => setLocalExpanded(v => !v)} className="p-1 -mr-1 rounded hover:bg-black/10 dark:hover:bg-white/10 transition-colors">
                           <DynamicIcon name="ChevronDown" className={`w-4 h-4 transition-transform ${localExpanded ? 'rotate-180' : ''}`} />
                        </button>
                     </div>
                     <div className={`overflow-hidden transition-all duration-300 ${localExpanded ? 'max-h-[2000px] opacity-100 pb-1' : 'max-h-0 opacity-0'}`}>
                        {item.children.map(child => {
                           if (child.type === 'group') {
                              return (
                                 <div key={child.id} className="mt-1">
                                    <div className="px-4 py-1 text-[10px] font-bold text-gray-400 uppercase tracking-widest pl-12">{child.label}</div>
                                    {child.children.map(sub => {
                                       // Determine if this sub-item is the active one
                                       let subActive = false;
                                       if (activePage === 'tenant_app_runner') {
                                          const uid = tenantAppRunnerProps?.appUid;
                                          const bid = tenantAppRunnerProps?.bundleId;
                                          const cid = tenantAppRunnerProps?.contentUid; subActive = sub.id === `app_${uid}` || sub.id === `bundle_${bid}` || sub.id === `proj_${uid}` || (cid && sub.id.endsWith(`_${cid}`));
                                       } else if (activePage === 'myapps_dashboard') {
                                          subActive = sub.id === `dash_${dashboardEditorState?.dashboardId}`;
                                       }
                                       return (
                                          <button
                                             key={sub.id}
                                             onClick={() => {
                                                if (sub.action) sub.action();
                                                else { handleBack(); setActivePage(sub.id); }
                                                setMobileSidebarOpen(false);
                                             }}
                                             className={`w-full flex items-start text-left px-4 py-2 pl-12 rounded-lg transition-colors ${subActive ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 font-medium' : 'hover:bg-black/5 dark:hover:bg-white/5 text-gray-600 dark:text-gray-300'
                                                }`}
                                          >
                                             <DynamicIcon name={sub.icon} className={`w-4 h-4 mr-2 mt-0.5 shrink-0 ${subActive ? 'text-blue-500' : 'opacity-70'}`} />
                                             <span className="text-sm break-words text-left min-w-0">{sub.label}</span>
                                             {subActive && <span className="ml-auto w-1.5 h-1.5 rounded-full bg-blue-500 shrink-0 mt-1" />}
                                          </button>
                                       );
                                    })}
                                 </div>
                              )
                           }
                           let childActive = activePage === child.id;
                           if (activePage === 'tenant_app_runner') {
                              const uid = tenantAppRunnerProps?.appUid;
                              const bid = tenantAppRunnerProps?.bundleId;
                              childActive = child.id === `app_${uid}` || child.id === `bundle_${bid}` || child.id === `proj_${uid}`;
                           } else if (activePage === 'myapps_dashboard') {
                              childActive = child.id === `dash_${dashboardEditorState?.dashboardId}`;
                           }

                           return (
                              <button
                                 key={child.id}
                                 onClick={() => {
                                    if (child.action) child.action();
                                    else { handleBack(); setActivePage(child.id); }
                                    setMobileSidebarOpen(false);
                                 }}
                                 className={`w-full flex items-start text-left px-4 py-2 pl-12 rounded-lg transition-colors ${childActive ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 font-medium' : 'hover:bg-black/5 dark:hover:bg-white/5 text-gray-600 dark:text-gray-300'
                                    }`}
                              >
                                 {child.icon && <DynamicIcon name={child.icon} className={`w-4 h-4 mr-2 ${childActive ? 'text-blue-500' : 'opacity-70'}`} />}
                                 <span className="text-sm break-words text-left min-w-0">{child.label}</span>
                                 {childActive && <span className="ml-auto w-1.5 h-1.5 rounded-full bg-blue-500 shrink-0" />}
                              </button>
                           )
                        })}
                     </div>
                  </div>
               );
            }

            return (
               <button
                  onClick={() => {
                     handleBack();
                     if (item.action) item.action();
                     else setActivePage(item.id);
                     setMobileSidebarOpen(false);
                  }}
                  className={`w-full flex items-center px-4 py-3 rounded-lg transition-colors duration-200 group ${isActive
                     ? 'bg-blue-600 text-white shadow-md'
                     : 'text-gray-600 dark:text-gray-300 hover:bg-black/5 dark:hover:bg-white/5'
                     }`}
                  title={!isSidebarOpen ? item.label : ''}
               >
                  <DynamicIcon name={item.icon || 'Circle'} className={`w-5 h-5 min-w-[20px] mr-3 overflow-visible transition-transform group-hover:scale-110 ${isActive ? 'text-white' : 'text-gray-500 dark:text-gray-400 group-hover:text-blue-600 dark:group-hover:text-blue-400'}`} />
                  <span className={`font-medium whitespace-nowrap transition-all duration-300 ${!isSidebarOpen ? 'opacity-0 w-0 overflow-hidden' : 'opacity-100'}`}>
                     {item.label}
                  </span>
               </button>
            );
         };
      }, [location, activePage, tenantAppRunnerProps, dashboardEditorState, isSidebarOpen]);

      const mainNavPages = [
         // { id: 'landing', label: 'Home', icon: 'Home' },
         { id: 'dashboard', label: 'Home', icon: 'Home' },
         { id: 'logs', label: 'Execution Status', icon: 'Loader' },
      ];

      const administrationPages = [
         { id: 'partners', label: 'Partners', icon: 'Briefcase' },
         { id: 'tenants', label: 'Tenants', icon: 'Users' },
         { id: 'users', label: 'Users', icon: 'UserCircle' },
         { id: 'roles_permissions', label: 'Roles & Permissions', icon: 'ShieldCheck' },
         { id: 'plans', label: 'Plans & Tiers', icon: 'BadgeDollarSign' },
         { id: 'credits', label: 'Credit Management', icon: 'CreditCard' },
         { id: 'support', label: 'Support Tickets', icon: 'LifeBuoy' },
         { id: 'demo_management', label: 'Public Demos', icon: 'Globe' },

         { id: 'platform_settings', label: 'Settings', icon: 'Settings' },
      ].filter(Boolean);

      // Support Agent navigavtions only Support Tickets (r8/r9/r10/r5)
      const isSupportAgent = ['r8', 'r9', 'r10', 'r5'].includes(currentUser?.roleId);
      const supportAgentPages = [
         { id: 'support', label: 'Support Tickets', icon: 'LifeBuoy' },
      ];

      const creatorNavPages = [
         { id: 'flows', label: 'Flows', icon: 'Workflow' },
         { id: 'ai-insight', label: 'Agent Insights', icon: 'Sparkles' },
         // { id: 'semantic-kb', label: 'Semantic Knowledge Base', icon: 'BookOpen' }, // MOVED: Now in Administration as SVB
         // { id: 'contents', label: 'AI/ML', icon: 'BrainCircuit' }, // HIDDEN: AI/ML page disabled (Scenario A)
         { id: 'dashboards', label: 'Dashboard', icon: 'LayoutDashboard' },
         { id: 'business_apps', label: 'Business Apps', icon: 'PackageCheck' },
      ].filter(Boolean);

      const appStudioNavPages = [
         { id: 'data-workspace', label: 'Unified Data Workspace', icon: 'DatabaseZap' },
      ].filter(Boolean);

      // 3.5 App Studio
      const appStudioItems = [
         tenantFeatures.unified_data_editor && {
            id: 'data-workspace',
            label: 'Unified Data Workspace',
            icon: 'DatabaseZap',
            action: () => setActivePage('data-workspace')
         }
      ].filter(Boolean);

      const dataNavPages = [
         { id: 'datasources', label: 'Data Sources', icon: 'CableIcon' },
         { id: 'datamodels', label: 'Data Models', icon: 'Database' },
         hasAccess('semantic-kb') && {
            id: 'semantic-kb',
            label: 'Knowledge base',
            icon: 'Database', // or BookOpen
            tooltip: 'Knowledge base',
            action: () => setActivePage('semantic-kb')
         },
         // { id: 'DatasetsPage', label: 'Datasets (old)', icon: 'Layers' },
         { id: 'automations', label: 'Scheduler', icon: 'WandSparkles' },
         { id: 'alerts', label: 'Alert Rules', icon: 'Bell' },
      ].filter(Boolean);



      // Tenant Specific Nav (replacing main nav for tenants, or appending?)
      // Consolidate into one list driven by roles
      const tenantHomePages = [
         { id: 'landing', label: 'Home', icon: 'Home' },
         { id: 'tasks', label: 'Task Center', icon: 'ListChecks' },
         { id: 'support', label: 'Support', icon: 'LifeBuoy' },
         { id: 'logs', label: 'Execution Logs', icon: 'ClipboardList' },
      ];


      // 2. Creator Studio Items
      const creatorItems = [
         // Unified Flows entry ÃƒÆ’Ã†â€™Ãƒâ€šÃ‚Â¢ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â€šÂ¬Ã…Â¡Ãƒâ€šÃ‚Â¬ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â‚¬Å¡Ã‚Â¬Ãƒâ€šÃ‚Â shown when any studio feature is enabled
         (tenantFeatures.data_studio || tenantFeatures.ml_studio || tenantFeatures.workflow_studio) && {
            id: 'flows',
            label: 'Flows',
            icon: 'Workflow',
            onClick: () => navigate('/flows')
         },

         // Legacy / Specific Tools
         tenantFeatures.dashboard_studio && {
            id: 'dashboards',
            label: 'Dashboards',
            icon: 'LayoutDashboard',
            onClick: () => navigate('/dashboards')
         },
         (tenantFeatures.ai_insight || tenantFeatures.AIINSIGHT_NLQ) && {
            id: 'ai-insight',
            label: 'Agent Insights',
            icon: 'Sparkles',
            action: () => setActivePage('ai-insight')
         },
      ].filter(Boolean);

      // 3. Data Settings
      const dataItems = [
         // Data Sources ÃƒÆ’Ã†â€™Ãƒâ€šÃ‚Â¢ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â€šÂ¬Ã…Â¡Ãƒâ€šÃ‚Â¬ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â‚¬Å¡Ã‚Â¬Ãƒâ€šÃ‚Â always available for all tenants (scoped to their own connections)
         {
            id: 'datasources',
            label: 'Data Sources',
            icon: 'CableIcon',
            action: () => setActivePage('datasources')
         },
         tenantFeatures.data_models && {
            id: 'datamodels',
            label: 'Data Models',
            icon: 'Database',
            onClick: () => navigate('/datamodels')
         },
         hasAccess('semantic-kb') && {
            id: 'semantic-kb',
            label: 'Knowledge base',
            icon: 'Database',
            tooltip: 'Knowledge base',
            action: () => setActivePage('semantic-kb')
         },
         // Data Explorer & Datasets - Optional, can link to Data Studio or be separate
         // Keeping them visible for now if Data Studio is enabled, or if explicitly needed.
         // For now, let's tie them to 'data_studio' (Data Studio) as core data features
         (tenantFeatures.data_studio && currentUser?.roleId === 'r1') && {
            id: 'dataexplorer',
            label: 'Data Explorer',
            icon: 'Search',
            onClick: () => navigate('/dataexplorer')
         },
         // tenantFeatures.data_studio && {
         //    id: 'datasets',
         //    label: 'Datasets',
         //    icon: 'FileText',
         //    onClick: () => navigate('/datasets')
         // },
         { id: 'automations', label: 'Scheduler', icon: 'WandSparkles' },
         { id: 'alerts', label: 'Alert Rules', icon: 'Bell' },
      ].filter(Boolean);

      // Accordion states are now in PlatformLayout top-level scope (accessed via closure)
      // to survive re-renders caused by state changes in the parent component.
      const canSeeCreator = creatorNavPages.some(page => hasAccess(page.id));
      const canSeeAppStudio = appStudioNavPages.some(page => hasAccess(page.id));
      const canSeeData = dataNavPages.some(page => hasAccess(page.id));


      // Helper to handle mutual exclusion for accordion behavior
      const handleSectionExpand = (section, isOpen) => {
         if (!isOpen) {
            // If handling close, just update local state
            switch (section) {
               case 'aidas_apps': setAidasAppsExpanded(false); break;
               case 'published_apps': setPublishedAppsExpanded(false); break;
               case 'my_apps': setMyAppsExpanded(false); break;
               case 'creator': setCreatorExpanded(false); break;
               case 'app_studio': setAppStudioExpanded(false); break;
               case 'data': setDataExpanded(false); break;
               case 'admin': setAdminExpanded(false); break;
               case 'settings': setSettingsExpanded(false); break;
            }
            return;
         }

         if (['aidas_apps', 'published_apps', 'my_apps', 'creator', 'app_studio', 'data'].includes(section)) {
            // Open target
            if (section === 'aidas_apps') setAidasAppsExpanded(true);
            if (section === 'published_apps') setPublishedAppsExpanded(true);
            if (section === 'my_apps') {
               setMyAppsExpanded(true);
               if (refreshContent) refreshContent(); // Auto-refresh list
            }
            if (section === 'creator') setCreatorExpanded(true);
            if (section === 'app_studio') setAppStudioExpanded(true);
            if (section === 'data') setDataExpanded(true);

            // Collapse Bottom
            setAdminExpanded(false);
            setSettingsExpanded(false);
         } else {
            // Opening Bottom Section (Admin or Settings)
            if (section === 'admin') {
               setAdminExpanded(true);
               setSettingsExpanded(false);
            }
            if (section === 'settings') {
               setSettingsExpanded(true);
               setAdminExpanded(false);
            }
         }
      };

      // Auto-collapse logic moved to PlatformLayout top-level scope to avoid Sidebar remount issues.

      // ... (Menu State for Collapsed Mode)
      const [settingsAnchorEl, setSettingsAnchorEl] = React.useState(null);
      const isSettingsMenuOpen = Boolean(settingsAnchorEl);
      const handleSettingsClick = (event) => setSettingsAnchorEl(event.currentTarget);
      const handleSettingsClose = () => setSettingsAnchorEl(null);

      const [adminAnchorEl, setAdminAnchorEl] = React.useState(null);
      const isAdminMenuOpen = Boolean(adminAnchorEl);
      const handleAdminClick = (event) => setAdminAnchorEl(event.currentTarget);
      const handleAdminClose = () => setAdminAnchorEl(null);


      return (
         <aside
            className={`fixed md:relative z-40 h-full flex-shrink-0 flex flex-col bg-white dark:bg-gray-800 transition-all duration-300 ${isSidebarOpen ? 'w-64' : 'w-20'
               } ${mobileSidebarOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0`}
            style={{ backgroundColor: theme.sidebarTheme.bg, color: theme.sidebarTheme.text }}
         >
            <div className={`flex items-center justify-between px-4 h-16 border-b`} style={{ borderColor: 'rgba(128, 128, 128, 0.2)' }}>
               <div className={`overflow-hidden transition-all ${!isSidebarOpen && 'w-0'}`}>
                  {isTenantUser ? (
                     <>
                        <span className="text-xl font-bold" style={{ color: theme.accentColor }}>{currentUser.tenantName || 'Home'}</span>
                        <span className="text-xs block text-gray-400">Workspace</span>
                     </>
                  ) : (
                     <>
                        <span className="text-xl font-bold" style={{ color: theme.accentColor }}>AIDAS</span>
                        <span className="text-xl font-bold"> Apps</span>
                     </>
                  )}
               </div>
               <button onClick={() => setSidebarOpen(!isSidebarOpen)} className={`p-1.5 rounded-md hover:bg-black/10 dark:hover:bg-white/10 hidden md:block`}>
                  <DynamicIcon name={isSidebarOpen ? 'ChevronLeft' : 'ChevronRight'} />
               </button>
            </div>

            <nav
               ref={(el) => {
                  // navScrollRef lives at PlatformLayout scope ÃƒÆ’Ã†â€™Ãƒâ€šÃ‚Â¢ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â€šÂ¬Ã…Â¡Ãƒâ€šÃ‚Â¬ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â‚¬Å¡Ã‚Â¬Ãƒâ€šÃ‚Â survives Sidebar's full DOM remount
                  if (el) el.scrollTop = navScrollRef.current;
               }}
               onScroll={(e) => {
                  navScrollRef.current = e.currentTarget.scrollTop;
               }}
               className={`flex-1 flex flex-col justify-between px-2 py-4 overflow-y-auto overflow-x-hidden ${isSidebarOpen ? 'scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600' : '[&::-webkit-scrollbar]:hidden'}`}>

               <div className="space-y-1">
                  {/* 1. Main Nav */}
                  {isTenantUser ? (
                     tenantHomePages.map(page => <RenderMenuItem key={page.id} item={page} />)
                  ) : isSupportAgent ? (
                     /* Support Agents (r8/r9/r10/r5) see only Support Tickets */
                     supportAgentPages.map(page => <RenderMenuItem key={page.id} item={page} />)
                  ) : (
                     mainNavPages.filter(p => hasAccess(p.id)).map(page => <RenderMenuItem key={page.id} item={page} />)
                  )}

                  {/* 2a. AIDAS Apps */}
                  {isTenantUser && aidasAppsMenu.length > 0 && (
                     isSidebarOpen ? (
                        <>
                           <button onClick={() => handleSectionExpand('aidas_apps', !isAidasAppsExpanded)} className="w-full flex items-center justify-between px-4 py-2 mt-4 hover:bg-black/5 dark:hover:bg-white/5 rounded transition-colors group">
                              <span className="text-s font-semibold uppercase opacity-100 transition-opacity text-[rgb(0,103,178)] dark:text-white">AIDAS Apps</span>
                              <DynamicIcon name="ChevronDown" className={`w-4 h-4 opacity-60 group-hover:opacity-100 transition-transform duration-300 ${isAidasAppsExpanded ? 'rotate-180' : ''}`} />
                           </button>
                           <div className={`space-y-1 transition-all duration-300 ease-in-out overflow-hidden ${isAidasAppsExpanded ? 'max-h-[5000px] opacity-100' : 'max-h-0 opacity-0'}`}>
                              {aidasAppsMenu.map(bundle => <RenderMenuItem key={bundle.id} item={bundle} />)}
                           </div>
                        </>
                     ) : (
                        aidasAppsMenu.map(bundle => <RenderMenuItem key={bundle.id} item={bundle} />)
                     )
                  )}

                  {/* 2b. Published Apps */}
                  {isTenantUser && publishedAppsMenu.length > 0 && (
                     isSidebarOpen ? (
                        <>
                           <button onClick={() => handleSectionExpand('published_apps', !isPublishedAppsExpanded)} className="w-full flex items-center justify-between px-4 py-2 mt-4 hover:bg-black/5 dark:hover:bg-white/5 rounded transition-colors group">
                              <span className="text-s font-semibold uppercase opacity-100 transition-opacity text-[rgb(0,103,178)] dark:text-white">Published Apps</span>
                              <DynamicIcon name="ChevronDown" className={`w-4 h-4 opacity-60 group-hover:opacity-100 transition-transform duration-300 ${isPublishedAppsExpanded ? 'rotate-180' : ''}`} />
                           </button>
                           <div className={`space-y-1 transition-all duration-300 ease-in-out overflow-hidden ${isPublishedAppsExpanded ? 'max-h-[5000px] opacity-100' : 'max-h-0 opacity-0'}`}>
                              {publishedAppsMenu.map(bundle => <RenderMenuItem key={bundle.id} item={bundle} />)}
                           </div>
                        </>
                     ) : (
                        publishedAppsMenu.map(bundle => <RenderMenuItem key={bundle.id} item={bundle} />)
                     )
                  )}

                  {/* 2c. My Apps */}
                  {isTenantUser && myAppsMenu.length > 0 && (
                     isSidebarOpen ? (
                        <>
                           <button onClick={() => handleSectionExpand('my_apps', !isMyAppsExpanded)} className="w-full flex items-center justify-between px-4 py-2 mt-4 hover:bg-black/5 dark:hover:bg-white/5 rounded transition-colors group">
                              <span className="text-s font-semibold uppercase opacity-100 transition-opacity text-[rgb(0,103,178)] dark:text-white">My Workspace</span>
                              <DynamicIcon name="ChevronDown" className={`w-4 h-4 opacity-60 group-hover:opacity-100 transition-transform duration-300 ${isMyAppsExpanded ? 'rotate-180' : ''}`} />
                           </button>
                           <div className={`space-y-1 transition-all duration-300 ease-in-out overflow-hidden ${isMyAppsExpanded ? 'max-h-[5000px] opacity-100' : 'max-h-0 opacity-0'}`}>
                              {myAppsMenu.map(bundle => <RenderMenuItem key={bundle.id} item={bundle} />)}
                           </div>
                        </>
                     ) : (
                        myAppsMenu.map(bundle => <RenderMenuItem key={bundle.id} item={bundle} />)
                     )
                  )}

                  {/* 3. Creator Studio (Tenants) */}
                  {isTenantUser && creatorItems.length > 0 && (
                     isSidebarOpen ? (
                        <>
                           <button onClick={() => handleSectionExpand('creator', !isCreatorExpanded)} className="w-full flex items-center justify-between px-4 py-2 mt-4 hover:bg-black/5 dark:hover:bg-white/5 rounded transition-colors group">
                              <span className="text-s font-semibold uppercase opacity-100 transition-opacity text-[rgb(0,103,178)] dark:text-white">Creator Studio</span>
                              <DynamicIcon name="ChevronDown" className={`w-4 h-4 opacity-60 group-hover:opacity-100 transition-transform duration-300 ${isCreatorExpanded ? 'rotate-180' : ''}`} />
                           </button>
                           <div className={`space-y-1 transition-all duration-300 ease-in-out overflow-hidden ${isCreatorExpanded ? 'max-h-[5000px] opacity-100' : 'max-h-0 opacity-0'}`}>
                              {creatorItems.map(page => <RenderMenuItem key={page.id} item={page} />)}
                           </div>
                        </>
                     ) : (
                        creatorItems.map(page => <RenderMenuItem key={page.id} item={page} />)
                     )
                  )}

                  {/* 3.5 App Studio (Tenants) */}
                  {isTenantUser && appStudioItems.length > 0 && (
                     isSidebarOpen ? (
                        <>
                           <button onClick={() => handleSectionExpand('app_studio', !isAppStudioExpanded)} className="w-full flex items-center justify-between px-4 py-2 mt-4 hover:bg-black/5 dark:hover:bg-white/5 rounded transition-colors group">
                              <span className="text-s font-semibold uppercase opacity-100 transition-opacity text-[rgb(0,103,178)] dark:text-white">App Studio</span>
                              <DynamicIcon name="ChevronDown" className={`w-4 h-4 opacity-60 group-hover:opacity-100 transition-transform duration-300 ${isAppStudioExpanded ? 'rotate-180' : ''}`} />
                           </button>
                           <div className={`space-y-1 transition-all duration-300 ease-in-out overflow-hidden ${isAppStudioExpanded ? 'max-h-[5000px] opacity-100' : 'max-h-0 opacity-0'}`}>
                              {appStudioItems.map(page => <RenderMenuItem key={page.id} item={page} />)}
                           </div>
                        </>
                     ) : (
                        appStudioItems.map(page => <RenderMenuItem key={page.id} item={page} />)
                     )
                  )}

                  {/* 3.7 DQ Studio section (Tenants) */}
                  {isTenantUser && isSidebarOpen && hasAccess('dq-profiler') && (
                     <>
                        <button
                           className="w-full flex items-center justify-between px-4 py-2 mt-4 hover:bg-black/5 dark:hover:bg-white/5 rounded transition-colors group"
                           onClick={() => setActivePage('dq-profiler')}
                        >
                           <span className="text-s font-semibold uppercase opacity-100 transition-opacity text-[rgb(0,103,178)] dark:text-white">DQ Studio</span>
                        </button>
                        <div className="space-y-1">
                           <RenderMenuItem key="dq-profiler" item={{ id: 'dq-profiler', label: 'Data Profiler', icon: 'ShieldCheck', action: () => setActivePage('dq-profiler') }} />
                        </div>
                     </>
                  )}

                  {/* 4. Data Settings (Tenants) */}
                  {isTenantUser && dataItems.length > 0 && (
                     isSidebarOpen ? (
                        <>
                           <button onClick={() => handleSectionExpand('data', !isDataExpanded)} className="w-full flex items-center justify-between px-4 py-2 mt-4 hover:bg-black/5 dark:hover:bg-white/5 rounded transition-colors group">
                              <span className="text-s font-semibold uppercase opacity-100 transition-opacity text-[rgb(0,103,178)] dark:text-white">Data Settings</span>
                              <DynamicIcon name="ChevronDown" className={`w-4 h-4 opacity-60 group-hover:opacity-100 transition-transform duration-300 ${isDataExpanded ? 'rotate-180' : ''}`} />
                           </button>
                           <div className={`space-y-1 transition-all duration-300 ease-in-out overflow-hidden ${isDataExpanded ? 'max-h-[5000px] opacity-100' : 'max-h-0 opacity-0'}`}>
                              {dataItems.map(page => <RenderMenuItem key={page.id} item={page} />)}
                           </div>
                        </>
                     ) : (
                        dataItems.map(page => <RenderMenuItem key={page.id} item={page} />)
                     )
                  )}


                  {/* 4. Creator Studio (Admin Only) */}
                  {!isTenantUser && isSidebarOpen ? (
                     canSeeCreator && (
                        <>
                           <button onClick={() => handleSectionExpand('creator', !isCreatorExpanded)} className="w-full flex items-center justify-between px-4 py-2 mt-4 hover:bg-black/5 dark:hover:bg-white/5 rounded transition-colors group">
                              <span className="text-s font-semibold uppercase opacity-100 group-hover:opacity-100 transition-opacity  text-[rgb(0,103,178)] dark:text-white">Creator Studio</span>
                              <DynamicIcon name="ChevronDown" className={`w-4 h-4 opacity-60 group-hover:opacity-100 transition-transform duration-300 ${isCreatorExpanded ? 'rotate-180' : ''}`} />
                           </button>
                           <div className={`space-y-1 transition-all duration-300 ease-in-out overflow-hidden ${isCreatorExpanded ? 'max-h-[5000px] opacity-100' : 'max-h-0 opacity-0'}`}>
                              {creatorNavPages.filter(p => hasAccess(p.id)).map(page => <RenderMenuItem key={page.id} item={page} />)}
                           </div>
                        </>
                     )
                  ) : (
                     !isTenantUser && creatorNavPages.filter(p => hasAccess(p.id)).map(page => <RenderMenuItem key={page.id} item={page} />)
                  )}

                  {/* 4.5 App Studio (Admin Only) */}
                  {!isTenantUser && isSidebarOpen ? (
                     canSeeAppStudio && (
                        <>
                           <button onClick={() => handleSectionExpand('app_studio', !isAppStudioExpanded)} className="w-full flex items-center justify-between px-4 py-2 mt-4 hover:bg-black/5 dark:hover:bg-white/5 rounded transition-colors group">
                              <span className="text-s font-semibold uppercase opacity-100 group-hover:opacity-100 transition-opacity  text-[rgb(0,103,178)] dark:text-white">App Studio</span>
                              <DynamicIcon name="ChevronDown" className={`w-4 h-4 opacity-60 group-hover:opacity-100 transition-transform duration-300 ${isAppStudioExpanded ? 'rotate-180' : ''}`} />
                           </button>
                           <div className={`space-y-1 transition-all duration-300 ease-in-out overflow-hidden ${isAppStudioExpanded ? 'max-h-[5000px] opacity-100' : 'max-h-0 opacity-0'}`}>
                              {appStudioNavPages.filter(p => hasAccess(p.id)).map(page => <RenderMenuItem key={page.id} item={page} />)}
                           </div>
                        </>
                     )
                  ) : (
                     !isTenantUser && canSeeAppStudio && appStudioNavPages.filter(p => hasAccess(p.id)).map(page => <RenderMenuItem key={page.id} item={page} />)
                  )}

                  {/* 4.7 DQ Studio (Admin Only) */}
                  {!isTenantUser && isSidebarOpen && hasAccess('dq-profiler') && (
                     <>
                        <button
                           className="w-full flex items-center justify-between px-4 py-2 mt-4 hover:bg-black/5 dark:hover:bg-white/5 rounded transition-colors group"
                           onClick={() => setActivePage('dq-profiler')}
                        >
                           <span className="text-s font-semibold uppercase opacity-100 group-hover:opacity-100 transition-opacity text-[rgb(0,103,178)] dark:text-white">DQ Studio</span>
                        </button>
                        <div className="space-y-1">
                           <RenderMenuItem key="dq-profiler-admin" item={{ id: 'dq-profiler', label: 'Data Profiler', icon: 'ShieldCheck', action: () => setActivePage('dq-profiler') }} />
                        </div>
                     </>
                  )}

                  {/* 5. Data Settings (Admin Only) */}
                  {!isTenantUser && isSidebarOpen ? (
                     canSeeData && (
                        <>
                           <button onClick={() => handleSectionExpand('data', !isDataExpanded)} className="w-full flex items-center justify-between px-4 py-2 mt-4 hover:bg-black/5 dark:hover:bg-white/5 rounded transition-colors group">
                              <span className="text-s font-semibold uppercase opacity-100 group-hover:opacity-100 transition-opacity  text-[rgb(0,103,178)] dark:text-white">Data Settings</span>
                              <DynamicIcon name="ChevronDown" className={`w-4 h-4 opacity-60 group-hover:opacity-100 transition-transform duration-300 ${isDataExpanded ? 'rotate-180' : ''}`} />
                           </button>
                           <div className={`space-y-1 transition-all duration-300 ease-in-out overflow-hidden ${isDataExpanded ? 'max-h-[5000px] opacity-100' : 'max-h-0 opacity-0'}`}>
                              {dataNavPages.filter(p => hasAccess(p.id)).map(page => <RenderMenuItem key={page.id} item={page} />)}
                           </div>
                        </>
                     )
                  ) : (
                     !isTenantUser && dataNavPages.filter(p => hasAccess(p.id)).map(page => <RenderMenuItem key={page.id} item={page} />)
                  )}
               </div>

               {/* Administration Group (Admin Only) */}
               {!isTenantUser && (
                  <div className="mt-auto pt-2">
                     {isSidebarOpen ? (
                        administrationPages.some(p => hasAccess(p.id)) && (
                           <>
                              <button
                                 onClick={() => handleSectionExpand('admin', !isAdminExpanded)}
                                 className="w-full flex items-center justify-between px-4 py-2 hover:bg-black/5 dark:hover:bg-white/5 rounded transition-colors group"
                              >
                                 <span className="text-s font-semibold uppercase opacity-100 group-hover:opacity-100 transition-opacity  text-[rgb(0,103,178)] dark:text-white">Administration</span>
                                 <DynamicIcon
                                    name="ChevronDown"
                                    className={`w-4 h-4 opacity-60 group-hover:opacity-100 transition-transform duration-300 ${isAdminExpanded ? 'rotate-180' : ''}`}
                                 />
                              </button>
                              <div className={`space-y-1 transition-all duration-300 ease-in-out overflow-hidden ${isAdminExpanded ? 'max-h-[5000px] opacity-100' : 'max-h-0 opacity-0'}`}>
                                 {administrationPages.filter(p => hasAccess(p.id)).map(page => <RenderMenuItem key={page.id} item={page} />)}
                              </div>
                           </>
                        )
                     ) : (
                        administrationPages.some(p => hasAccess(p.id)) && (
                           <>
                              <div
                                 className="flex justify-center py-2 my-2 cursor-pointer opacity-70 hover:opacity-100 hover:bg-black/5 dark:hover:bg-white/5 rounded transition-colors"
                                 onClick={handleAdminClick}
                              >
                                 <DynamicIcon name="Users" className="w-6 h-6" />
                              </div>
                              <Menu
                                 anchorEl={adminAnchorEl}
                                 open={isAdminMenuOpen}
                                 onClose={handleAdminClose}
                                 anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
                                 transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                              >
                                 {administrationPages.filter(page => hasAccess(page.id)).map(page => (
                                    <MenuItem key={page.id} onClick={() => {
                                       handleAdminClose();
                                       handleBack();
                                       navigate(`/${page.id}`);
                                    }}>
                                       <ListItemIcon>
                                          <DynamicIcon name={page.icon} size={20} />
                                       </ListItemIcon>
                                       <ListItemText primary={page.label} />
                                    </MenuItem>
                                 ))}
                              </Menu>
                           </>
                        )
                     )}
                  </div>
               )}

               {/* Sticky / Bottom Organization Settings Accordion (Tenant Only) */}
               {isTenantUser && (
                  <div className="pt-2">
                     {isSidebarOpen ? (
                        <>
                           <button
                              onClick={() => handleSectionExpand('settings', !isSettingsExpanded)}
                              className="w-full flex items-center justify-between px-4 py-2 hover:bg-black/5 dark:hover:bg-white/5 rounded transition-colors group"
                           >
                              <span className="text-s font-semibold uppercase opacity-100 transition-opacity text-[rgb(0,103,178)] dark:text-white">Settings</span>
                              <DynamicIcon
                                 name="ChevronDown"
                                 className={`w-4 h-4 opacity-60 group-hover:opacity-100 transition-transform duration-300 ${isSettingsExpanded ? 'rotate-180' : ''}`}
                              />
                           </button>
                           <div className={`space-y-1 transition-all duration-300 ease-in-out overflow-hidden ${isSettingsExpanded ? 'max-h-[5000px] opacity-100' : 'max-h-0 opacity-0'}`}>
                              <RenderMenuItem item={{ id: 'users', label: 'Users', icon: 'Users' }} />
                              <RenderMenuItem item={{ id: 'settings', label: 'Settings', icon: 'Settings' }} />
                              <RenderMenuItem item={{ id: 'credits', label: 'Credit Usage', icon: 'CreditCard', action: () => setActivePage('credits') }} />
                           </div>
                        </>
                     ) : (
                        <>
                           <div
                              className="flex justify-center py-2 my-2 cursor-pointer opacity-70 hover:opacity-100 hover:bg-black/5 dark:hover:bg-white/5 rounded transition-colors"
                              onClick={handleSettingsClick}
                           >
                              <DynamicIcon name="Settings" className="w-6 h-6" />
                           </div>
                           <Menu
                              anchorEl={settingsAnchorEl}
                              open={isSettingsMenuOpen}
                              onClose={handleSettingsClose}
                              anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
                              transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                           >
                              {/* Tenant Popup Menu */}
                              <MenuItem onClick={() => { handleSettingsClose(); handleBack(); navigate('/settings'); }}>
                                 <ListItemIcon><DynamicIcon name="Settings" size={20} /></ListItemIcon>
                                 <ListItemText primary="Tenant Settings" />
                              </MenuItem>
                              <MenuItem onClick={() => { handleSettingsClose(); setActivePage('credits'); }}>
                                 <ListItemIcon><DynamicIcon name="CreditCard" size={20} /></ListItemIcon>
                                 <ListItemText primary="Credit Usage" />
                              </MenuItem>
                           </Menu>
                        </>
                     )}
                  </div>
               )}
            </nav>
         </aside>
      );
   };

   return (
      <div className="flex h-screen bg-gray-100 dark:bg-gray-900 font-sans" style={{ fontFamily: theme.font }}>
         {/* --- PASSWORD EXPIRY TOAST / BANNER --- */}


         <div className={`fixed inset-0 bg-black/60 z-30 md:hidden ${mobileSidebarOpen ? 'block' : 'hidden'}`} onClick={() => setMobileSidebarOpen(false)}></div>
         {currentUser && <Sidebar />}
         <div className="flex-1 flex flex-col overflow-hidden">

            {/* Header */}
            <header className={`flex-shrink-0 flex items-center px-4 h-16 border-b bg-white dark:bg-gray-800`}
               style={{ backgroundColor: theme.headerTheme.bg, borderColor: 'rgba(0, 0, 0, 0.1)' }}>
               <button onClick={() => setMobileSidebarOpen(true)} className="text-gray-500 dark:text-gray-400 md:hidden">
                  <DynamicIcon name="Menu" className="w-6 h-6" />
               </button>

               {/* Branding when Sidebar is Collapsed */}
               {!isSidebarOpen && (
                  <div className="hidden md:flex items-center ml-4 transition-opacity duration-300">
                     <span className="text-xl font-bold" style={{ color: theme.accentColor }}>{isTenantUser ? (currentUser.tenantName || 'Home') : 'AIDAS'}</span>
                     <span className="text-xl font-bold text-gray-800 dark:text-white ml-1.5">{!isTenantUser && 'Apps'}</span>
                  </div>
               )}

               <div className="flex items-center space-x-4 ml-auto">
                  <button onClick={() => setTheme(t => ({ ...t, mode: t.mode === 'light' ? 'dark' : 'light' }))} className="text-gray-500 dark:text-gray-400">
                     <DynamicIcon name={theme.mode === 'light' ? 'Moon' : 'Sun'} />
                  </button>
                  <AlertNotificationCenter />
                  {currentUser && (
                     <UserProfileMenu
                        user={currentUser}
                        onLogout={logout}
                        token={token}
                        onProfileClick={() => handleNavigate('profile')}
                     />
                  )}
               </div>
            </header>

            <main className={`flex-1 ${(['app_runner', 'tenant_app_runner', 'platform_settings', 'settings', 'support'].includes(activePage) || (activePage === 'dashboards' && dashboardEditorState) || (activePage === 'myapps_dashboard' && dashboardEditorState) || onboardingTenantId) ? 'overflow-hidden p-0' : 'overflow-auto'} ${(!onboardingTenantId && !['app_runner', 'tenant_app_runner', 'platform_settings', 'settings', 'support'].includes(activePage) && !(activePage === 'dashboards' && dashboardEditorState) && !(activePage === 'myapps_dashboard' && dashboardEditorState)) ? 'p-6' : ''}`}>
               {currentUser ? renderPage() : <LoadingSpinner />}
            </main>
         </div>
      </div >
   );
};

export default PlatformLayout;
